import { Link } from "react-router-dom";
import { Shield, ArrowRight, Check, Mic, FileText, Users, FolderKanban, ClipboardCheck, PenSquare } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import PflegeKiDemoLoop from "@/components/site/PflegeKiDemoLoop";

/**
 * Pflege-KI Funnel-Sektion · Kinetic Editorial.
 * Eine durchgehende Espresso-Szene: Story → 3 Schritte → zwei Pakete (199 € / 349 €).
 */

const STEPS = [
  {
    n: "01",
    title: "Sprechen statt schreiben",
    desc: "Deine Pflegekraft erzählt der App einfach, was sie beim Hausbesuch gesehen hat — per Mikrofon, direkt am Patientenbett. Kein Tippen nötig.",
  },
  {
    n: "02",
    title: "Die KI schreibt die Doku",
    desc: "Aus dem Gesprochenen macht die KI fertige Fachdokumente: SIS, Maßnahmenplan, Risikoeinschätzung. Sie erfindet dabei nichts — sie nutzt nur, was gesagt wurde.",
  },
  {
    n: "03",
    title: "Du prüfst, unterschreibst — fertig",
    desc: "Die PDL liest gegen, unterschreibt digital am Bildschirm und lädt das fertige, MDK-sichere PDF herunter. Aus 2 Stunden Schreibtisch werden 25 Minuten.",
  },
];

const FEATURES = [
  { icon: FolderKanban, t: "Digitale Patientenmappe (12 Sektionen)" },
  { icon: ClipboardCheck, t: "MD-Prüfung vorab simulieren" },
  { icon: Mic, t: "Diktat per Mikrofon (Whisper)" },
  { icon: Users, t: "Zugänge fürs ganze Team" },
  { icon: PenSquare, t: "Digitale Unterschrift & Freigabe" },
  { icon: FileText, t: "Fertiges PDF für den Prüfer" },
];

const PACKAGES = [
  {
    id: "pflege-ki-kompakt",
    kicker: "Bis 10 Mitarbeiter",
    name: "Pflege-KI Kompakt",
    price: "199 €",
    note: "/ Monat · netto",
    bullets: ["1 PDL + bis zu 10 Mitarbeiter-Accounts", "Alle Funktionen · ohne Ausnahme", "Monatlich kündbar · keine Setup-Gebühr"],
    featured: true,
  },
  {
    id: "pflege-ki-team",
    kicker: "Ab 11 Mitarbeiter",
    name: "Pflege-KI Team",
    price: "349 €",
    note: "/ Monat · netto",
    bullets: ["Unbegrenzt Mitarbeiter-Accounts", "1:1 Onboarding-Call für PDL + Kernteam", "Jahres-Pflichtfortbildungen inklusive"],
    featured: false,
  },
];

export default function HeldenCampus() {
  return (
    <section id="campus" className="text-sand-800 py-28 md:py-40 relative overflow-hidden" style={{ backgroundColor: "#F6E8DC" }} data-testid="pflege-ki-section">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── 1 · Editorial Opener ── */}
        <Reveal>
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-terra" />
            <span className="font-poster text-[11px] uppercase tracking-[0.3em] font-medium text-terra">
              Pflege-KI Assistenz · Für ambulante Pflegedienste
            </span>
          </div>
          <h2 className="font-magazine font-medium text-[2.8rem] sm:text-[3.8rem] lg:text-[4.6rem] leading-[1.05] tracking-tight max-w-5xl text-sand-800">
            Die Doku macht<br />
            <span className="text-terra italic">jetzt die KI.</span>
          </h2>
          <p className="mt-8 text-sand-700 text-[16px] leading-[1.75] max-w-2xl">
            Ganz einfach erklärt: Deine Pflegekräfte <strong>sprechen</strong> ihre Beobachtungen
            ins Handy — und die KI macht daraus die <strong>fertige Pflegedokumentation</strong>.
            Du prüfst, unterschreibst digital, fertig. Kein Papierkrieg, keine Nachtschichten
            am Schreibtisch.
          </p>
        </Reveal>

        {/* ── 2 · So funktioniert's + Demo ── */}
        <div className="mt-16 md:mt-20 grid lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-16 items-start">
          <div className="space-y-0">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 100}>
                <div className="flex items-start gap-6 py-7 border-t border-sand-800/10" data-testid={`pki-step-${s.n}`}>
                  <span className="font-magazine text-[2.6rem] leading-none text-terra shrink-0 w-16">{s.n}</span>
                  <div>
                    <h3 className="font-magazine font-medium text-[1.4rem] leading-tight text-sand-800">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sand-600 text-[14.5px] leading-[1.7]">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}

            {/* Feature strip */}
            <Reveal delay={320}>
              <div className="mt-6 flex flex-wrap gap-2.5" data-testid="pki-feature-strip">
                {FEATURES.map((f) => (
                  <span key={f.t} className="inline-flex items-center gap-2 rounded-full bg-white/75 border border-terra/25 px-4 h-9 text-[12.5px] text-sand-700">
                    <f.icon className="h-3.5 w-3.5 text-terra" strokeWidth={1.8} />
                    {f.t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={150}>
            <PflegeKiDemoLoop />
          </Reveal>
        </div>

        {/* ── 3 · Funnel-Bridge ── */}
        <Reveal>
          <div className="mt-24 md:mt-28 border-t border-sand-800/10 pt-14">
            <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
              <div className="md:col-span-8">
                <h3 className="font-magazine font-medium text-[2.2rem] sm:text-[3rem] lg:text-[3.4rem] leading-[1.08] text-sand-800">
                  Ein Werkzeug. Zwei Pakete.<br />
                  <span className="text-terra italic">Der einzige Unterschied: deine Teamgröße.</span>
                </h3>
              </div>
              <div className="md:col-span-4">
                <p className="text-sand-600 text-[14px] leading-[1.7] md:text-right">
                  Beide Pakete enthalten <strong className="text-sand-800">alle Funktionen</strong>.
                  Du zahlst nur für die Größe deines Teams — monatlich kündbar.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* ── 4 · Die zwei Pakete ── */}
        <div className="grid md:grid-cols-2 gap-5 lg:gap-7 max-w-5xl">
          {PACKAGES.map((p, idx) => (
            <Reveal key={p.id} delay={idx * 120}>
              <PackageCard pkg={p} />
            </Reveal>
          ))}
        </div>

        {/* ── 5 · Schnupper + Kurs-Strip ── */}
        <Reveal delay={250}>
          <div className="mt-10 max-w-5xl grid md:grid-cols-2 gap-5">
            <Link
              to="/pflege-ki-testen"
              data-testid="pki-schnupper-link"
              className="group flex flex-col justify-between gap-4 rounded-2xl bg-white/80 border border-sand-800/10 px-6 md:px-8 py-6 hover:border-terra transition-colors duration-300"
            >
              <div>
                <span className="font-poster text-[11px] uppercase tracking-[0.26em] font-medium text-terra">
                  Noch unsicher?
                </span>
                <div className="mt-1 font-magazine font-medium text-[1.3rem] text-sand-800 leading-tight">
                  7 Tage alles testen · 29 € einmalig · kein Abo
                </div>
              </div>
              <span className="inline-flex items-center gap-2 font-poster text-[12px] uppercase tracking-[0.18em] font-medium text-terra">
                Test-Woche starten
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={2.2} />
              </span>
            </Link>
            <a
              href="#kurse"
              data-testid="pki-fuehrerschein-link"
              className="group flex flex-col justify-between gap-4 rounded-2xl bg-terra text-cream-50 px-6 md:px-8 py-6 hover:bg-terra-dark transition-colors duration-300"
            >
              <div>
                <span className="font-poster text-[11px] uppercase tracking-[0.26em] font-medium text-cream-100/85">
                  Lernen + Software in einem
                </span>
                <div className="mt-1 font-magazine font-medium text-[1.3rem] leading-tight">
                  Pflege-KI Führerschein · 8 Module + 6 Monate Zugang · 1.990 €
                </div>
              </div>
              <span className="inline-flex items-center gap-2 font-poster text-[12px] uppercase tracking-[0.18em] font-medium">
                Zum Kurs
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={2.2} />
              </span>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PackageCard({ pkg }) {
  const featured = pkg.featured;
  return (
    <div
      data-testid={`pki-package-${pkg.id}`}
      className={`relative h-full flex flex-col p-8 md:p-9 rounded-2xl ${
        featured
          ? "bg-terra text-cream-50 shadow-[0_24px_54px_-24px_rgba(224,120,86,0.55)]"
          : "bg-white/80 border border-sand-800/10 text-sand-800"
      }`}
    >
      {featured && (
        <span className="absolute top-0 right-0 bg-espresso-deep text-cream-50 text-[10px] uppercase tracking-[0.22em] font-bold px-3.5 py-2 rounded-bl-2xl rounded-tr-2xl">
          Meistgewählt
        </span>
      )}
      <div className={`font-poster text-[11px] uppercase tracking-[0.28em] font-medium ${featured ? "text-cream-100/85" : "text-terra"}`}>
        {pkg.kicker}
      </div>
      <h4 className="mt-4 font-magazine font-medium text-[1.9rem] md:text-[2.2rem] leading-none">
        {pkg.name}
      </h4>
      <div className="mt-7 flex items-baseline gap-2">
        <span className="font-magazine text-[3.4rem] leading-none">{pkg.price}</span>
        <span className={`text-[13px] ${featured ? "text-cream-100/75" : "text-sand-500"}`}>{pkg.note}</span>
      </div>
      <ul className={`mt-7 pt-6 space-y-3 flex-1 border-t ${featured ? "border-cream-50/25" : "border-sand-800/10"}`}>
        {pkg.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-[14px] leading-snug">
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-cream-50" : "text-terra"}`} strokeWidth={2.6} />
            <span className={featured ? "text-cream-50" : "text-sand-700"}>{b}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/preise"
        data-testid={`pki-package-cta-${pkg.id}`}
        className={`mt-8 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full font-poster text-[13px] tracking-[0.14em] uppercase font-medium transition-colors duration-300 ${
          featured
            ? "bg-cream-50 text-terra hover:bg-espresso-deep hover:text-cream-50"
            : "border-2 border-terra/50 text-terra hover:bg-terra hover:text-cream-50 hover:border-terra"
        }`}
      >
        {pkg.name} starten
        <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
      </Link>
    </div>
  );
}

/** Behalten – wird auf Kurs-Landing-Pages als Sidebar-Karte verwendet. */
export function CampusBadge() {
  return (
    <Link
      to="/campus"
      data-testid="campus-badge-coursepage"
      className="group flex items-start gap-4 rounded-2xl bg-espresso text-cream-50 p-5 md:p-6 hover:bg-espresso-card transition-colors duration-300"
    >
      <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-terra text-white">
        <Shield className="h-5 w-5" strokeWidth={1.8} />
      </span>
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-brand-rose">
            Bonus inklusive
          </span>
          <span className="inline-flex items-center text-[11px] uppercase tracking-[0.18em] font-bold text-espresso bg-terra px-2 py-0.5 rounded-full">
            Wert 588 €/Jahr
          </span>
        </div>
        <h4 className="font-magazine text-lg md:text-xl font-semibold leading-tight">
          Dickies-Campus während des Kurses + 6 Monate gratis
        </h4>
        <p className="mt-1.5 text-[13px] text-cream-100/75 leading-relaxed">
          MDK-Schutzschild · Schatzkiste · monatliches Live-Briefing.
        </p>
        <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-terra group-hover:text-brand-rose transition-colors">
          Was du im Campus bekommst
          <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" strokeWidth={2.2} />
        </span>
      </div>
    </Link>
  );
}
